/**
 * Two to four short options side by side, one chosen, such as vanilla or chocolate. A radio group
 * drawn as one bar: the chosen option sits on a thumb with an accent edge, so the choice shows in
 * shape as well as colour, in both themes.
 *
 * Arrow keys move and choose, as in any radio group. With `manual` they only move and Space or
 * Enter chooses, for a choice that saves the moment it is made. With nothing chosen yet, no option
 * has a thumb and Tab lands on the first.
 *
 * Named by a visible `label`, or by a heading on the page through `labelledBy`.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useId, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import { choiceStyle, moveWithoutChoosing } from "./choice.ts";
import { focusRing } from "./focus.ts";
import { Spinner } from "./Spinner.tsx";
import { visuallyHidden } from "./visuallyHidden.ts";
import { textStyle } from "./textStyle.ts";
const styles = {
  legend: {
    kmVPX3: "x1717udv",
    k1K539: "xz4076n",
    kMwMTN: "xm8xz4y",
    $$css: true
  },
  input: {
    kysU6D: "xjyslct",
    kVAEAm: "x10l6tqk",
    kpwlN0: "x10a8y8t",
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    kogj98: "x1ghz6dp",
    kaIpWk: "x8wwnw1",
    kMzoRj: "xdq7b4a",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x9r1u3d",
    kWkggS: "xjbqb8w x14uvmqf",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  inputOn: {
    kWkggS: "x3oldu3 xvf97pm",
    kVAM5u: "xtfhg7g",
    $$css: true
  },
  inputDisabled: {
    kkrTdU: "x1h6gzvc",
    kWkggS: "xjbqb8w",
    $$css: true
  },
  label: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kzQI83: "x1iyjqo2",
    kAzted: "x1io08vv xlivvtk",
    kOIVth: "xskm3c9",
    kg3NbH: "xerji9v",
    kMwMTN: "xm8xz4y",
    k9WMMc: "x2b8uid",
    kfzvcC: "x47corl",
    $$css: true
  },
  labelOn: {
    kMwMTN: "xm8xz4y",
    $$css: true
  },
  labelDisabled: {
    kMwMTN: "x16nvyyk",
    $$css: true
  }
};
export interface SegmentOption<Value extends string> {
  readonly value: Value;
  readonly label: string;
  readonly disabled?: boolean;
  readonly testId?: string;
}
interface Shared<Value extends string> {
  readonly options: readonly SegmentOption<Value>[];
  readonly value: Value | null;
  readonly onChange: (value: Value) => void;
  readonly manual?: boolean;
  /** The id of text on the page that describes the choice, such as what is still missing. */
  readonly describedBy?: string | undefined;
  /** While a choice saves: busy, ignoring presses, and keeping focus where it is. */
  readonly loading?: boolean;
  readonly disabled?: boolean;
  /** Stretches the bar to its container, with equal segments. */
  readonly fullWidth?: boolean;
  readonly name?: string;
  readonly testId?: string;
}
export type SegmentedControlProps<Value extends string> = Shared<Value> & ({
  readonly label: string;
  readonly labelledBy?: never;
} | {
  readonly labelledBy: string;
  readonly label?: never;
});
export function SegmentedControl<Value extends string>(props: SegmentedControlProps<Value>) {
  const made = useId();
  const name = props.name ?? made;
  const legendId = `${made}-legend`;

  /* The option just chosen shows as chosen, with a spinner, while the page saves it. */
  const [asked, setAsked] = useState<Value | null>(null);
  const saving = props.loading === true;
  useEffect(() => {
    if (!saving) setAsked(null);
  }, [saving]);
  const choose = (value: string) => {
    const picked = props.options.find(option => option.value === value);
    if (picked === undefined || saving) return;
    setAsked(picked.value);
    props.onChange(picked.value);
  };
  const chosen = saving && asked !== null ? asked : props.value;
  return <>
      <fieldset role="radiogroup" aria-labelledby={props.labelledBy ?? legendId} aria-busy={props.loading === true || undefined} aria-describedby={props.describedBy} data-testid={props.testId} className="x78zum5 xdt5ytf xskm3c9 xeuugli x1ghz6dp x1717udv xc342km">
        {props.label !== undefined && <legend id={legendId} {...stylex.props(textStyle.label, styles.legend)}>
            {props.label}
          </legend>}
        <div {...{
        0: {
          className: "x78zum5 xeq5yr9 x193iq5w x1xxeqd3 x15xv0s5 x9f619 x1fbwzx x14ba300 xdq7b4a x1y0btm7 x1j7zvbq"
        },
        1: {
          className: "x78zum5 x193iq5w x1xxeqd3 x15xv0s5 x9f619 x1fbwzx x14ba300 xdq7b4a x1y0btm7 x1j7zvbq xh8yej3"
        }
      }[!!(props.fullWidth === true) << 0]}>
          {props.options.map(option => {
          const id = `${made}-${option.value}`;
          const on = chosen === option.value;
          const off = props.disabled === true || option.disabled === true;
          return <span key={option.value} {...{
            0: {
              className: "x1n2onr6 x78zum5 x1iyjqo2 xeuugli"
            },
            1: {
              className: "x1n2onr6 x78zum5 x1iyjqo2 xeuugli x1r8uery"
            }
          }[!!(props.fullWidth === true) << 0]}>
                <input id={id} type="radio" name={name} value={option.value} checked={on} disabled={off} aria-disabled={props.loading === true || undefined} onChange={() => choose(option.value)} onKeyDown={props.manual === true ? event => moveWithoutChoosing(event, choose) : undefined} data-testid={option.testId} {...stylex.props(styles.input, on && styles.inputOn, off && styles.inputDisabled, props.loading === true && choiceStyle.busy, focusRing)} />
                <label htmlFor={id} {...stylex.props(textStyle.label, styles.label, on && styles.labelOn, off && styles.labelDisabled)}>
                  {saving && asked === option.value && <span aria-hidden="true" className="x3nfvp2 xeh5slm x3ycs4o x2lah0s">
                      <Spinner />
                    </span>}
                  {option.label}
                </label>
              </span>;
        })}
        </div>
      </fieldset>
      {saving && <span role="status" {...stylex.props(visuallyHidden.hidden)}>
          Saving
        </span>}
    </>;
}